// /stats123 — rizzup's stats page, rebuilt for the one-time funnel.
//
// Same plain table cloutclub uses: ten columns, 1px black borders, 5px padding,
// stacked cells, yellow gross / green net / red-green estimate, an editable ad
// spend cell, and a Chart.js net-revenue bar.
//
// What differs is the data, not the layout: refunds are real rows from the
// `refunds` table rather than a flat percentage, ad spend comes from this
// funnel's own `funnel_adspend` series rather than the account-wide `fbads`,
// and the page starts at the offer's first sale.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { API_BASE_URL } from '../config/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const n2 = (v) => Number(v || 0).toFixed(2);
// Ad spend is typed by hand, so echo back exactly what was stored — padding 1638 to
// 1638.00 on every reload made the field look edited when it was not.
const raw = (v) => { const n = Number(v || 0); return n > 0 ? String(n) : ''; };

// Both ncLTNP figures are day-one profit plus a per-customer lifetime value
// multiplied across new customers, and that multiplication is the whole
// argument between them. Rendered rather than left to the native title
// attribute, which waits about a second before appearing and is easy to miss
// entirely on a dense table.
function Popover({ children, label }) {
	const [show, setShow] = useState(false);
	return (
		<span
			style={{ position: 'relative', display: 'inline-block' }}
			onMouseEnter={() => setShow(true)}
			onMouseLeave={() => setShow(false)}
			// Tap opens it on a phone, where there is no hover at all.
			onClick={() => setShow((v) => !v)}
		>
			<span style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>{label}</span>
			{show && (
				<span style={{
					position: 'absolute', bottom: '135%', left: '50%', transform: 'translateX(-50%)',
					background: '#222', color: '#fff', padding: '6px 9px', borderRadius: 6,
					fontSize: 12, lineHeight: 1.4, fontWeight: 'normal', whiteSpace: 'nowrap',
					zIndex: 1000, boxShadow: '0 2px 8px rgba(0,0,0,.35)', pointerEvents: 'none',
				}}>
					{children}
				</span>
			)}
		</span>
	);
}

// C1/C2/C3 are the day's net after ads once each refill cycle has closed:
// day-one contribution, minus ad spend, plus what this day's buyers add by that
// cycle, priced by their own starting pack from viralgpt's measured refill
// payment and retention. The popup shows that arithmetic per pack, with the
// /charts123 cohort LTV for the same day alongside for reference.
// `model` is projection.ltv[cycle - 1].
const PACK_ORDER = ['starter', 'basic', 'pro', 'unknown'];
function packDetail(m, cycle) {
	if (m.blended) return 'blend of all packs';
	const how = cycle === 1
		? `${m.paidRate}% of ${m.users} paid refill 1, repeat $${n2(m.repeat)}`
		: `${m.retention}% of ${m.users} kept paying, repeat $${n2(m.repeat)}`;
	return `${how}; /charts123 LTV $${m.ltv === null ? '—' : n2(m.ltv)}`;
}
function CycleFigure({ value, cycle, packs, model, day1Net, adSpend, costRates }) {
	if (value === null || value === undefined) {
		return (
			<Popover label="—">
				<span>Fewer than 50 viralgpt buyers of a pack are {model?.days ?? ''} days old yet, so cycle {cycle} is not measured.</span>
			</Popover>
		);
	}
	const rows = PACK_ORDER
		.map((pack) => ({ pack, count: (packs || {})[pack] || 0, m: model?.byPack?.[pack] }))
		.filter((r) => r.count > 0 && r.m);
	const added = rows.reduce((n, r) => n + r.count * r.m.net, 0);
	return (
		<Popover label={`$${n2(value)}`}>
			{rows.length === 0 ? <span>No new customers on this day. C{cycle} = day-one net − ad spend.</span> : (
				<span className="pop">
					<span className="pop-h">C{cycle}: net after ads once refill cycle {cycle} has closed (day {model.days})</span>
					<table>
						<tbody>
							<tr><td className="pk">Day-one net</td><td className="n" /><td className="x" /><td className="v">${n2(day1Net)}</td></tr>
							<tr><td className="pk">Ad spend</td><td className="n" /><td className="x" /><td className="v">−${n2(adSpend)}</td></tr>
							{rows.map(({ pack, count, m }) => (
								<tr key={pack}>
									<td className="pk">{pack === 'unknown' ? 'Unattributed' : pack}</td>
									<td className="n">{count}</td>
									<td className="x">× ${n2(m.net)} <span className="dim">({packDetail(m, cycle)})</span></td>
									<td className="v">${n2(count * m.net)}</td>
								</tr>
							))}
							<tr className="tot">
								<td className="pk">C{cycle}</td>
								<td className="n" />
								<td className="x dim">+${n2(added)}</td>
								<td className="v"><strong>${n2(value)}</strong></td>
							</tr>
						</tbody>
					</table>
					{costRates && (
						<span className="pop-f">
							Per buyer after day one, cumulative: refills × {Math.round((1 - costRates.refill) * 1000) / 10}% + repeat purchases × {Math.round((1 - costRates.other) * 1000) / 10}%.<br />
							/charts123 LTV is gross and includes day one, so it is shown for reference, not added.
						</span>
					)}
				</span>
			)}
		</Popover>
	);
}


// The Revenue column's headline: exactly the three lines stacked under it. Ad
// revenue is a channel split of Initial reported in its own column, so adding
// it here would double-count and make the figure disagree with its breakdown.
const revSum = (r) => Number(r.initial || 0) + Number(r.topups || 0) + Number(r.renewal || 0);

export default function Stats123() {
	// Rendered at both /stats123 and /hadi. /hadi is the restricted view: Sep-Oct
	// 2026 only, and renewal revenue off as the default and only mode.
	const isHadi = window.location.pathname === '/hadi';
	const [days, setDays] = useState(7);
	const [renewal, setRenewal] = useState(isHadi ? 'off' : 'on');
	const [data, setData] = useState(null);
	const [err, setErr] = useState('');
	const [loading, setLoading] = useState(true);
	const [spend, setSpend] = useState({});
	const [saving, setSaving] = useState('');
	const [stale, setStale] = useState(false);

	const load = () => {
		setLoading(true);
		const params = { days, renewal };
		if (isHadi) params.scope = 'hadi';
		return axios.get(`${API_BASE_URL}/admin/stats123`, { params })
			.then((r) => { setData(r.data); setErr(''); setSpend({}); setStale(false); })
			.catch((e) => setErr(e.response?.data?.error || e.message))
			.finally(() => setLoading(false));
	};
	useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days, renewal]);

	// Safari's tab strip shows the title, not the URL, and every route here inherits
	// index.html's marketing title. Name the tab after the path so this page is
	// findable among a dozen open tabs; restore the default when routing away.
	useEffect(() => {
		const previous = document.title;
		document.title = window.location.pathname === '/hadi' ? 'viralgpt.com/hadi' : 'viralgpt.com/stats123';
		return () => { document.title = previous; };
	}, []);

	const saveSpend = async (date, value) => {
		if (value === '' || value === undefined) return;
		setSaving(date);
		try {
			await axios.post(`${API_BASE_URL}/admin/stats123/adspend`, { date, adspend: Number(value) });
			// Saved, but the derived columns (MER, net, margin) are now behind. Reloading
			// here yanked the table out from under an in-progress row of entries, so the
			// refresh is left to the Refresh button.
			setStale(true);
		} catch (e) {
			setErr(e.response?.data?.error || e.message);
		} finally {
			setSaving('');
		}
	};

	const rows = data?.rows || [];
	const t = data?.totals;
	const A = data?.assumptions;
	const proj = data?.projection;


	const chartData = useMemo(() => {
		const src = [...rows].reverse();
		return {
			labels: src.map((r) => r.date.slice(5)),
			datasets: [{
				label: 'Net Revenue',
				data: src.map((r) => r.net),
				backgroundColor: src.map((r) => (r.net >= 0 ? '#00ff14' : '#ff9999')),
				borderColor: '#000',
				borderWidth: 1,
			}],
		};
	}, [rows]);

	// cloutclub styles its buttons with exactly one rule; everything else is the
	// browser's own button.
	const btn = (on) => (on ? { backgroundColor: '#4CAF50', color: 'white', fontWeight: 'bold' } : {});

	return (
		<div className="s123">
			{/* table-layout:fixed is what lets ten columns fit a phone without a
			    sideways scroll — without it one long cell sets the table's width. */}
			{/* cloutclub's page is raw HTML with one four-line stylesheet — no
			    Tailwind preflight, no Bootstrap reset, no app CSS. This app loads
			    all three globally, and they reach into any element rendered here:
			    preflight alone sets border-collapse, strips list and heading
			    margins, makes buttons inherit font, and forces border-box.

			    `all: revert` rolls every property on this subtree back to the
			    browser's own defaults, discarding every author rule from those
			    libraries. What follows re-applies only the handful of rules
			    cloutclub actually declares. Inline styles still win, which is how
			    the yellow and green cells survive.

			    Only the media query departs, and it has to: cloutclub's page
			    carries no viewport meta tag, so a phone renders it at ~980px and
			    zooms out — visually shrinking the text. This app sets
			    width=device-width globally, so the same effect has to come from
			    the font size instead. */}
			<style>{`
				.s123, .s123 * { all: revert; }
				.s123 { padding: 15px; }
				.s123 table, .s123 th, .s123 td { border: 1px solid black; padding: 5px; }
				.s123 table { border-collapse: collapse; width: 100%; table-layout: fixed; }
				.s123 td, .s123 th { vertical-align: middle; overflow-wrap: normal; word-break: normal; hyphens: none; }
				.s123 input.spend { width: 100%; box-sizing: border-box; }

				/* The popup is inside .s123, so the page's own table rules were
				   reaching it: width:100% + table-layout:fixed forced four equal
				   columns inside a box sized to its content, which is what crushed
				   the figures together, and the 1px cell border drew a grid over a
				   tooltip. Sized by content here, spaced by explicit gutters. */
				.s123 .pop { display: block; }
				.s123 .pop .pop-h { display: block; margin-bottom: 7px; color: #bbb; }
				.s123 .pop table { width: auto; table-layout: auto; border: 0; font-size: 12px; }
				.s123 .pop td { border: 0; padding: 3px 0; white-space: nowrap; text-align: left; }
				.s123 .pop td.pk { padding-right: 18px; text-transform: capitalize; }
				.s123 .pop td.n { padding-right: 14px; text-align: right; }
				.s123 .pop td.x { padding-right: 18px; color: #bbb; }
				.s123 .pop td.v { text-align: right; }
				.s123 .pop td.dim { color: #bbb; }
				.s123 .pop .dim { color: #999; }
				.s123 .pop .pop-f { display: block; margin-top: 7px; color: #999; }
				.s123 .pop tr.tot td { border-top: 1px solid #666; padding-top: 6px; }
				@media (max-width: 820px) {
					.s123 { padding: 6px; }
					.s123 table { font-size: 8px; }
					.s123 th, .s123 td { padding: 2px; }
					.s123 input.spend { font-size: 8px; padding: 1px; }
					.s123 .pop table { font-size: 11px; }
					.s123 .pop td { padding: 2px 0; }
					.s123 .pop td.pk { padding-right: 12px; }
					.s123 .pop td.n { padding-right: 10px; }
					.s123 .pop td.x { padding-right: 12px; }
				}
			`}</style>

			<h2 style={{ marginTop: 0 }}>ViralGPT — One-Time Funnel</h2>

			<div style={{ marginBottom: 10 }}>
				{[7, 14, 30, 90].map((d) => (
					<button key={d} style={btn(days === d)} onClick={() => setDays(d)}>{d} days</button>
				))}
				<span style={{ display: 'inline-block', width: 20 }} />
				{!isHadi && <button style={btn(renewal === 'on')} onClick={() => setRenewal('on')}>Renewals on</button>}
				{!isHadi && <button style={btn(renewal === 'off')} onClick={() => setRenewal('off')}>Renewals off</button>}
				<span style={{ display: 'inline-block', width: 20 }} />
				<button style={btn(false)} onClick={load} disabled={loading}>Refresh</button>
				{stale && <span style={{ marginLeft: 8, fontSize: 13 }}>Ad spend saved — refresh to recalculate.</span>}
			</div>

			{err && <div style={{ background: '#ffe6e6', border: '1px solid #cc0000', padding: 8, marginBottom: 10, fontSize: 13 }}>{err}</div>}
			{loading && <div style={{ fontSize: 13, marginBottom: 10 }}>Loading…</div>}

			{t && !loading && (
				<>
					<table>
						<thead>
							<tr>
								<th>Date</th>
								<th>Revenue</th>
								<th>Ad Revenue</th>
								<th style={{ backgroundColor: 'yellow' }}>Gross Revenue</th>
								<th>Product Costs</th>
								<th>Ad Costs</th>
								<th>Processor Costs</th>
								<th style={{ backgroundColor: '#00ff14' }}>Net Revenue</th>
								<th>New Subs</th>
								<th>Total Estimated Revenue</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((r) => (
								<tr key={r.date}>
									<td>{r.date}</td>
									<td>
										${n2(revSum(r))}<br />
										<strong>Initial:</strong> ${n2(r.initial)}<br />
										<strong>TopUp:</strong> ${n2(r.topups)}
										{!isHadi && <><br /><strong>Renewal:</strong> ${n2(r.renewal)}</>}
									</td>
									<td>${n2(r.ad)}</td>
									<td style={{ backgroundColor: 'yellow' }}><strong>${n2(r.gross)}</strong></td>
									<td>
										-${n2(r.productCosts)}<br />
										Bonus: -${n2(r.bonusCost)}
									</td>
									<td>
										-$<input
											className="spend"
											type="number"
											step="0.01"
											min="0"
											inputMode="decimal"
											placeholder="0"
											title="Type this day's ad spend, then press Enter"
											value={spend[r.date] !== undefined ? spend[r.date] : raw(r.adSpend)}
											disabled={saving === r.date}
											onChange={(e) => setSpend((p) => ({ ...p, [r.date]: e.target.value }))}
											onBlur={(e) => saveSpend(r.date, e.target.value)}
											onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
										/>
										<br />MER: {r.mer === null ? '—' : r.mer}
									</td>
									<td>
										Fees: -${n2(r.processorFees)}<br />
										CBs: -${n2(r.chargebacks)}<br />
										Refunds: -${n2(r.refunds)}
									</td>
									<td style={{ backgroundColor: '#00ff14' }}><strong>${n2(r.net)}</strong><br />{r.margin}%</td>
									<td>+{r.ncCount}<br />CPS: {r.ncCpa === null ? '—' : '$' + n2(r.ncCpa)}</td>
									<td style={{ backgroundColor: (r.ncC3 ?? r.ncC2 ?? r.ncC1) < 0 ? '#ff9999' : '#90ee90' }}>
										<strong>ncCPA:</strong> ${n2(r.ncCpa)}<br />
										<strong>C1:</strong>{' '}
										<CycleFigure value={r.ncC1} cycle={1} packs={r.ncPacks} model={proj?.ltv?.[0]} day1Net={r.ncDay1Net} adSpend={r.adSpend} costRates={proj?.costRates} /><br />
										<strong>C2:</strong>{' '}
										<CycleFigure value={r.ncC2} cycle={2} packs={r.ncPacks} model={proj?.ltv?.[1]} day1Net={r.ncDay1Net} adSpend={r.adSpend} costRates={proj?.costRates} /><br />
										<strong>C3:</strong>{' '}
										<CycleFigure value={r.ncC3} cycle={3} packs={r.ncPacks} model={proj?.ltv?.[2]} day1Net={r.ncDay1Net} adSpend={r.adSpend} costRates={proj?.costRates} />
									</td>
								</tr>
							))}
							<tr>
								<td><strong>TOTAL</strong></td>
								<td>
									${n2(revSum(t))}<br />
									<strong>Initial:</strong> ${n2(t.initial)}<br />
									<strong>TopUp:</strong> ${n2(t.topups)}
									{!isHadi && <><br /><strong>Renewal:</strong> ${n2(t.renewal)}</>}
								</td>
								<td>${n2(t.ad)}</td>
								<td style={{ backgroundColor: 'yellow' }}><strong>${n2(t.gross)}</strong></td>
								<td>
									-${n2(t.productCosts)}<br />
									Bonus: -${n2(t.bonusCost)}
								</td>
								<td>-${n2(t.adSpend)}<br />MER: {t.mer === null ? '—' : t.mer}</td>
								<td>
									Fees: -${n2(t.processorFees)}<br />
									CBs: -${n2(t.chargebacks)}<br />
									Refunds: -${n2(t.refunds)}
								</td>
								<td style={{ backgroundColor: '#00ff14' }}><strong>${n2(t.net)}</strong><br />{t.margin}%</td>
								<td>+{t.ncCount}<br />CPS: {t.ncCpa === null ? '—' : '$' + n2(t.ncCpa)}</td>
								<td style={{ backgroundColor: (t.ncC3 ?? t.ncC2 ?? t.ncC1) < 0 ? '#ff9999' : '#90ee90' }}>
									<strong>ncCPA:</strong> ${n2(t.ncCpa)}<br />
									<strong>C1:</strong>{' '}
									<CycleFigure value={t.ncC1} cycle={1} packs={t.ncPacks} model={proj?.ltv?.[0]} day1Net={t.ncDay1Net} adSpend={t.adSpend} costRates={proj?.costRates} /><br />
									<strong>C2:</strong>{' '}
									<CycleFigure value={t.ncC2} cycle={2} packs={t.ncPacks} model={proj?.ltv?.[1]} day1Net={t.ncDay1Net} adSpend={t.adSpend} costRates={proj?.costRates} /><br />
									<strong>C3:</strong>{' '}
									<CycleFigure value={t.ncC3} cycle={3} packs={t.ncPacks} model={proj?.ltv?.[2]} day1Net={t.ncDay1Net} adSpend={t.adSpend} costRates={proj?.costRates} /><br />
									<strong>AOV (day 1):</strong> ${n2(t.ncCount ? t.ncRevenue / t.ncCount : 0)}
								</td>
							</tr>
						</tbody>
					</table>

					<div style={{ maxWidth: 750, marginTop: 25 }}>
						<Bar
							data={chartData}
							options={{
								responsive: true,
								plugins: { legend: { display: false }, title: { display: true, text: 'Net Revenue' } },
								scales: { y: { beginAtZero: true } },
							}}
						/>
					</div>

					<div style={{ marginTop: 20, fontSize: 12, lineHeight: 1.7, maxWidth: 900 }}>
						One-time front-end offer only, from its first sale — legacy /50off subscribers and the old ad account are excluded.
						Ad spend is typed into the Ad Costs cell and saved per day; a blank cell means nothing has been entered.
						Fees {Math.round(A.processorRate * 100)}%, chargebacks {Math.round(A.chargebackRate * 100)}% and refunds {Math.round(A.refundRate * 100)}% in the Net column are assumptions at rizzup's daily rates.
						{proj?.ltv && (
							<> <strong>C1, C2 and C3</strong> are this day's new customers' net after ad spend once refill cycle 1 (day 7), 2 (day 35)
							and 3 (day 63) have closed — cumulative, so each includes the one before. Day one is the day's actual first-24-hour
							revenue. After that each customer is priced by their starting pack from viralgpt's own buyers: cycle 1 is what buyers at
							least 9 days old paid in refills and repeat purchases; each later cycle carries that refill forward by the share of payers
							who kept paying, plus that window's repeat purchases. Refills net at {Math.round((1 - proj.costRates.refill) * 1000) / 10}%
							(nothing ships), everything else at {Math.round((1 - proj.costRates.other) * 1000) / 10}%. A cycle shows "—" until every pack
							has 50 buyers old enough to measure it. Hover any figure for that day's pack arithmetic and the /charts123 LTV beside it.
							{' '}Net per customer after day one, by C1 / C2 / C3: {['starter', 'basic', 'pro'].map((pack, i) => (
								<span key={pack}>
									{i > 0 ? '; ' : ''}<strong>{pack}</strong> {proj.ltv.map((m) => (m.byPack[pack].net === null ? '—' : '$' + n2(m.byPack[pack].net))).join(' / ')}
								</span>
							))}.
							{proj.walletBillingFrom && (
								<> Wallet buyers whose first renewal came due before <strong>{proj.walletBillingFrom}</strong> are left out of every
								measurement: that path was leaving most due renewals with neither a charge nor a failure recorded. Wallet cohorts from
								that date onward are counted; card buyers are never excluded.</>
							)}</>
						)}
						Actual refunds over this window were <strong>${n2(t.refundsActual)}</strong> across {t.refundCount} order{t.refundCount === 1 ? '' : 's'}, against ${n2(t.refunds)} assumed.
					</div>
				</>
			)}
		</div>
	);
}
